import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2 } from 'lucide-react';

/**
 * TranslatedText Component
 * 
 * Automatically translates text based on global language selection.
 * Use this component to wrap any text that should be translated.
 * 
 * Usage:
 *   <TranslatedText text="Your English text here" />
 *   <TranslatedText text={dynamicText} fallback="Loading..." />
 *   <TranslatedText text="Action required" as="span" className="text-red-600" />
 * 
 * Features:
 * - Automatic translation based on global locale
 * - Caching to prevent redundant API calls
 * - Graceful fallback to English on failure
 * - Loading state indicator
 * - Preserves business terms in English
 */
const TranslatedText = ({ 
  text, 
  fallback = null, 
  as: Component = 'span',
  className = '',
  showLoading = false,
  cacheKey = null,
  ...props 
}) => {
  const { locale, translate, isEnglish } = useLanguage();
  const [translatedText, setTranslatedText] = useState(text);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const doTranslation = async () => {
      // If English or no text, just use original
      if (isEnglish || !text) {
        setTranslatedText(text);
        return;
      }

      setIsLoading(true);
      try {
        const result = await translate(text, cacheKey);
        if (mounted) {
          setTranslatedText(result);
        }
      } catch (err) {
        if (mounted) {
          setTranslatedText(text); // Fallback to English
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    doTranslation();

    return () => {
      mounted = false;
    };
  }, [text, locale, isEnglish, translate, cacheKey]);

  // Show fallback while loading initial translation
  if (isLoading && showLoading) {
    return fallback || (
      <Component className={`inline-flex items-center gap-1 ${className}`} {...props}>
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="opacity-50">{text}</span>
      </Component>
    );
  }

  return (
    <Component className={className} {...props}>
      {translatedText || text}
    </Component>
  );
};

/**
 * TranslatedSection Component
 * 
 * Translates multiple children texts at once.
 * Useful for translating entire sections with multiple text elements.
 */
export const TranslatedSection = ({ 
  children, 
  className = '',
  ...props 
}) => {
  const { languageConfig } = useLanguage();
  
  return (
    <div 
      className={className}
      style={{ fontFamily: languageConfig?.font || 'inherit' }}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * usePageTranslation Hook
 * 
 * Hook for translating multiple static texts on a page.
 * Useful for section headings, labels, and static content.
 * 
 * Usage:
 *   const { t, isLoading } = usePageTranslation({
 *     title: 'Dashboard',
 *     subtitle: 'Business Overview',
 *     actionRequired: 'Action Required'
 *   });
 *   
 *   return <h1>{t.title}</h1>
 */
export const usePageTranslation = (staticTexts = {}) => {
  const { locale, translate, isEnglish } = useLanguage();
  const [translations, setTranslations] = useState(staticTexts);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const translateAll = async () => {
      if (isEnglish) {
        setTranslations(staticTexts);
        return;
      }

      setIsLoading(true);
      const results = { ...staticTexts };
      
      try {
        for (const [key, text] of Object.entries(staticTexts)) {
          if (mounted && text) {
            results[key] = await translate(text, `page:${locale}:${key}`);
          }
        }
        if (mounted) {
          setTranslations(results);
        }
      } catch (err) {
        // Keep original texts on error
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    translateAll();

    return () => {
      mounted = false;
    };
  }, [locale, isEnglish, translate]);

  return { t: translations, isLoading };
};

export default TranslatedText;
